import type { ReactNode } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from './authStore';
import { NAV_ITEMS, activeNavItem, subNavFor } from './nav';
import { useTheme } from './useTheme';
import { Button, Badge } from '../components/ui';
import { Icon } from '../components/Icon';
import { TabGroup } from '../components/TabGroup';
import { Modal } from '../components/feedback';
import { VerificationBanner } from '../components/VerificationBanner';
import { AssistantWidget } from '../components/AssistantWidget';

/**
 * 控制台外壳。
 *
 * 版式照阿里云控制台的实拍取数：**顶栏 + 一级导航两行**（没有左侧栏），
 * 两行都是纯白、顶栏底部一条 1px 分隔线，内容区是软蓝渐变底。
 * 二级页面用标签组，而不是再嵌一层侧栏。
 */
export function AppShell({ children }: { children: ReactNode }) {
  const session = useAuthStore((state) => state.session);
  const logout = useAuthStore((state) => state.logout);
  const switchOrg = useAuthStore((state) => state.switchOrg);
  const location = useLocation();
  const navigate = useNavigate();
  const { isDark, toggle } = useTheme();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [accountOpen, setAccountOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  // 路由变化就收起所有浮层，避免「点了链接抽屉还开着」。
  useEffect(() => {
    setDrawerOpen(false);
    setAccountOpen(false);
    setSearchOpen(false);
  }, [location.pathname]);

  // Ctrl/⌘ + K 唤起站内搜索。
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setSearchOpen(true);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  const navItems = useMemo(
    () => NAV_ITEMS.filter((item) => session?.permissions.includes(item.permission)),
    [session],
  );

  const active = activeNavItem(location.pathname);
  const subNav = subNavFor(location.pathname).filter(
    (item) => item.permission === undefined || session?.permissions.includes(item.permission),
  );

  return (
    <div className="console-wash flex min-h-full flex-col">
      {/* ---------------------------------------------------------------- 顶栏 */}
      <header className="sticky top-0 z-30 border-b border-chrome bg-panel">
        <div className="mx-auto flex h-[38px] max-w-[1216px] items-center gap-2 px-3 sm:px-4">
          <button
            type="button"
            onClick={() => setDrawerOpen(true)}
            className="rounded p-1.5 text-fg-muted hover:bg-nest-2 hover:text-fg lg:hidden"
            aria-label="打开导航"
          >
            <Icon name="menu" className="h-4.5 w-4.5" />
          </button>

          <Link to="/" className="flex items-center gap-1.5 whitespace-nowrap">
            <span className="text-[15px] font-semibold tracking-tight text-fg">Havorix</span>
            <span className="text-[11px] text-fg-subtle">™</span>
          </Link>

          {/* 组织切换：对应阿里云顶栏的「工作台」位 */}
          <span className="hidden items-center gap-1.5 rounded-control bg-nest-2 px-2.5 py-1 text-[13px] text-fg sm:inline-flex">
            <Icon name="building" className="h-3.5 w-3.5 text-fg-muted" />
            <span className="max-w-[10rem] truncate">{session?.tenant.name}</span>
          </span>

          <div className="flex flex-1 justify-center px-2">
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              className="hidden h-8 w-full max-w-md items-center gap-2 rounded-control border border-line-strong bg-panel px-3 text-[13px] text-fg-placeholder hover:border-brand sm:flex"
            >
              <Icon name="search" className="h-3.5 w-3.5" />
              搜索功能与页面
              <span className="ml-auto rounded border border-line px-1 text-[11px] text-fg-subtle">⌘K</span>
            </button>
            <button
              type="button"
              onClick={() => setSearchOpen(true)}
              className="rounded p-1.5 text-fg-muted hover:bg-nest-2 sm:hidden"
              aria-label="搜索"
            >
              <Icon name="search" className="h-4.5 w-4.5" />
            </button>
          </div>

          <div className="flex items-center gap-1">
            <a
              href="https://havorix.cn/contact"
              target="_blank"
              rel="noreferrer"
              className="hidden rounded p-1.5 text-fg-muted hover:bg-nest-2 hover:text-fg sm:block"
              title="联系我们"
            >
              <Icon name="help" className="h-4.5 w-4.5" />
            </a>
            <button
              type="button"
              onClick={toggle}
              className="rounded p-1.5 text-fg-muted hover:bg-nest-2 hover:text-fg"
              aria-label={isDark ? '切换到浅色主题' : '切换到深色主题'}
              title={isDark ? '切换到浅色主题' : '切换到深色主题'}
            >
              <Icon name={isDark ? 'sun' : 'moon'} className="h-4.5 w-4.5" />
            </button>

            <div className="relative">
              <button
                type="button"
                onClick={() => setAccountOpen((open) => !open)}
                aria-expanded={accountOpen}
                className="flex items-center gap-1.5 rounded-control px-1.5 py-1 hover:bg-nest-2"
              >
                <span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand text-[11px] font-medium text-white">
                  {session?.user.name.slice(0, 1) ?? '·'}
                </span>
                <span className="hidden max-w-[8rem] truncate text-[13px] text-fg sm:inline">
                  {session?.user.name}
                </span>
                <Icon name="chevron-down" className="h-3.5 w-3.5 text-fg-subtle" />
              </button>

              {accountOpen ? (
                <>
                  <button
                    type="button"
                    aria-label="关闭账号菜单"
                    className="fixed inset-0 z-30 cursor-default"
                    onClick={() => setAccountOpen(false)}
                    tabIndex={-1}
                  />
                  <AccountMenu
                    onSwitchOrg={async (tenantId) => {
                      setAccountOpen(false);
                      await switchOrg(tenantId);
                      navigate('/');
                    }}
                    onLogout={async () => {
                      setAccountOpen(false);
                      await logout();
                      navigate('/login');
                    }}
                  />
                </>
              ) : null}
            </div>
          </div>
        </div>

        {/* -------------------------------------------------------- 一级导航 */}
        <nav className="mx-auto hidden max-w-[1216px] gap-1 px-3 sm:flex sm:px-4" aria-label="主导航">
          {navItems.map((item) => {
            const selected = active?.to === item.to;
            return (
              <NavLink
                key={item.to}
                to={item.to}
                className={`flex h-[38px] items-center gap-1.5 border-b-2 px-3 text-[13px] transition-colors ${
                  selected
                    ? 'border-brand font-medium text-brand'
                    : 'border-transparent text-fg-muted hover:text-fg'
                }`}
              >
                <Icon name={item.icon} className="h-4 w-4" />
                {item.label}
              </NavLink>
            );
          })}
        </nav>
      </header>

      {/* -------------------------------------------------------------- 内容 */}
      <main className="mx-auto w-full max-w-[1216px] flex-1 px-3 py-5 sm:px-4 sm:py-6">
        <VerificationBanner />
        {subNav.length > 1 ? (
          <TabGroup
            className="mb-5 max-w-full"
            items={subNav.map((item) => ({ id: item.to, label: item.label }))}
            active={location.pathname}
            onChange={(to) => navigate(to)}
          />
        ) : null}
        {children}
      </main>

      <footer className="mx-auto w-full max-w-[1216px] px-3 pb-6 text-xs text-fg-subtle sm:px-4">
        Havorix™ 控制台 · 本页所有操作均会记录在操作记录中
      </footer>

      {/* 移动端抽屉 */}
      {drawerOpen ? (
        <div className="fixed inset-0 z-40 lg:hidden">
          <button
            type="button"
            aria-label="关闭导航"
            className="absolute inset-0 bg-black/40"
            onClick={() => setDrawerOpen(false)}
          />
          <div className="relative h-full w-[16rem] max-w-[80vw] overflow-y-auto bg-panel p-4">
            <div className="mb-4 flex items-center justify-between">
              <span className="text-[15px] font-semibold">Havorix™</span>
              <Button size="sm" variant="ghost" onClick={() => setDrawerOpen(false)} title="关闭">
                <Icon name="close" className="h-4 w-4" />
              </Button>
            </div>
            <nav className="space-y-1" aria-label="主导航">
              {navItems.map((item) => (
                <NavLink
                  key={item.to}
                  to={item.to}
                  className={({ isActive }) =>
                    `flex items-center gap-2 rounded-control px-3 py-2.5 text-sm ${
                      isActive ? 'bg-brand-soft font-medium text-brand' : 'text-fg hover:bg-nest-2'
                    }`
                  }
                >
                  <Icon name={item.icon} className="h-4 w-4" />
                  <span className="flex-1">{item.label}</span>
                  <Icon name="chevron-right" className="h-3.5 w-3.5 text-fg-subtle" />
                </NavLink>
              ))}
            </nav>
            {subNav.length > 1 ? (
              <div className="mt-4 border-t border-line pt-4">
                <p className="mb-2 text-xs text-fg-subtle">本栏目</p>
                <div className="space-y-1">
                  {subNav.map((item) => (
                    <NavLink
                      key={item.to}
                      to={item.to}
                      className={({ isActive }) =>
                        `block rounded-control px-3 py-2 text-[13px] ${
                          isActive ? 'text-brand' : 'text-fg-muted hover:bg-nest-2'
                        }`
                      }
                    >
                      {item.label}
                    </NavLink>
                  ))}
                </div>
              </div>
            ) : null}
          </div>
        </div>
      ) : null}

      <SearchPalette open={searchOpen} onClose={() => setSearchOpen(false)} />
      <AssistantWidget />
    </div>
  );
}

/* ------------------------------------------------------------------ */

function AccountMenu({
  onSwitchOrg,
  onLogout,
}: {
  onSwitchOrg: (tenantId: string) => void;
  onLogout: () => void;
}) {
  const session = useAuthStore((state) => state.session);
  if (!session) return null;

  const others = session.memberships.filter((item) => item.tenantId !== session.tenant.id);

  return (
    <div className="absolute right-0 z-40 mt-2 w-64 rounded-card border border-line bg-panel p-2">
      <div className="border-b border-line px-2 pb-2">
        <p className="truncate text-[13px] font-medium text-fg">{session.user.name}</p>
        <p className="truncate text-xs text-fg-muted">{session.user.email}</p>
        <p className="mt-1 text-xs text-fg-subtle">
          {session.tenant.name} · 角色 {ROLE_LABEL[session.role]}
        </p>
      </div>

      <Link
        to="/security"
        className="mt-1 flex items-center gap-2 rounded-control px-2 py-2 text-[13px] text-fg hover:bg-nest-2"
      >
        <Icon name="shield" className="h-4 w-4 text-fg-muted" />
        安全设置
      </Link>

      {others.length > 0 ? (
        <div className="mt-1 border-t border-line pt-1">
          <p className="px-2 py-1 text-xs text-fg-subtle">切换到其它组织</p>
          {others.map((item) => (
            <button
              key={item.tenantId}
              type="button"
              onClick={() => onSwitchOrg(item.tenantId)}
              className="flex w-full items-center gap-2 rounded-control px-2 py-2 text-left text-[13px] text-fg hover:bg-nest-2"
            >
              <Icon name="building" className="h-4 w-4 text-fg-muted" />
              <span className="flex-1 truncate">{item.tenantName}</span>
              <Badge>{ROLE_LABEL[item.role]}</Badge>
            </button>
          ))}
        </div>
      ) : null}

      <button
        type="button"
        onClick={onLogout}
        className="mt-1 flex w-full items-center gap-2 rounded-control border-t border-line px-2 py-2 text-left text-[13px] text-fg hover:bg-nest-2"
      >
        <Icon name="logout" className="h-4 w-4 text-fg-muted" />
        退出登录
      </button>
    </div>
  );
}

const ROLE_LABEL: Record<string, string> = {
  owner: '所有者',
  admin: '管理员',
  member: '成员',
  viewer: '观察员',
};

/** 站内搜索：只是把导航目的地列出来过滤，不是全文检索。 */
function SearchPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const session = useAuthStore((state) => state.session);
  const navigate = useNavigate();
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!open) setQuery('');
  }, [open]);

  const targets = useMemo(() => {
    const allowed = NAV_ITEMS.filter((item) => session?.permissions.includes(item.permission));
    const flat = allowed.flatMap((item) => [
      { to: item.to, label: item.label, description: item.description },
      ...(subNavFor(item.to).length > 1
        ? subNavFor(item.to)
            .filter((sub) => sub.permission === undefined || session?.permissions.includes(sub.permission))
            .map((sub) => ({ to: sub.to, label: sub.label, description: item.description }))
        : []),
    ]);
    // 去重（一级与二级可能同路径）
    const seen = new Set<string>();
    return flat.filter((item) => (seen.has(item.to) ? false : (seen.add(item.to), true)));
  }, [session]);

  const results = useMemo(() => {
    const keyword = query.trim().toLowerCase();
    if (keyword === '') return targets;
    return targets.filter(
      (item) =>
        item.label.toLowerCase().includes(keyword) || item.description.toLowerCase().includes(keyword),
    );
  }, [query, targets]);

  return (
    <Modal open={open} title="站内搜索" onClose={onClose} size="md">
      <input
        autoFocus
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="输入页面名称，例如「会话」「审计」「接入」"
        className="h-10 w-full rounded-control border border-line-strong bg-panel px-3 text-sm text-fg placeholder:text-fg-placeholder"
      />
      <ul className="mt-3 max-h-72 space-y-1 overflow-y-auto">
        {results.length === 0 ? (
          <li className="px-2 py-6 text-center text-[13px] text-fg-muted">没有匹配的页面</li>
        ) : (
          results.map((item) => (
            <li key={item.to}>
              <button
                type="button"
                onClick={() => {
                  navigate(item.to);
                  onClose();
                }}
                className="flex w-full items-center gap-3 rounded-control px-2 py-2 text-left hover:bg-nest-2"
              >
                <span className="text-[13px] font-medium text-fg">{item.label}</span>
                <span className="truncate text-xs text-fg-muted">{item.description}</span>
              </button>
            </li>
          ))
        )}
      </ul>
    </Modal>
  );
}
